import { useMutation, useQueries, useQuery } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createReportExplorerClient } from './api';
import type { ReportRequest } from './model';
import type { CreateReportSharePayload } from './reportShare';
import { reportFilterDefinitions } from './reportFilters';
import type { ReportFilterField } from './reportEnums';
import { ReportExplorerScope, ReportFilterLookupMode } from './reportEnums';

export enum ReportQueryKind {
  Chart = 'CHART',
  ChartTimeShift = 'CHART_TIME_SHIFT',
  Table = 'TABLE',
}

export const reportExplorerQueryKey = ['reporting', 'report-explorer'] as const;

function useReportExplorerClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return createReportExplorerClient({
    config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
    accessToken: session.accessToken,
  });
}

export function useReportQuery(
  kind: ReportQueryKind,
  request: ReportRequest,
  enabled: boolean,
  scope = ReportExplorerScope.Standard,
  agencyId?: string,
) {
  const client = useReportExplorerClient();
  return useQuery({
    queryKey: [...reportExplorerQueryKey, scope, agencyId ?? '', kind, request],
    queryFn: () => client.run(request, scope, agencyId),
    enabled: enabled && (scope !== ReportExplorerScope.Agency || Boolean(agencyId)),
  });
}

export function useCreateReportShareMutation() {
  const client = useReportExplorerClient();
  // react-doctor-disable-next-line react-doctor/query-mutation-missing-invalidation -- creates a one-off share URL; no cached query lists report shares
  return useMutation({
    mutationFn: (payload: CreateReportSharePayload) => client.createShare(payload),
  });
}

export function useReportShareQuery(urlHash: string | undefined) {
  const client = useReportExplorerClient();
  return useQuery({
    queryKey: [...reportExplorerQueryKey, 'share', urlHash ?? ''],
    queryFn: () => {
      if (!urlHash) throw new Error('Report share hash is required.');
      return client.getShare(urlHash);
    },
    enabled: Boolean(urlHash),
    retry: false,
  });
}

export interface ReportQueryDescriptor {
  kind: ReportQueryKind;
  request: ReportRequest;
  enabled: boolean;
}

export function useReportQueries(descriptors: readonly ReportQueryDescriptor[]) {
  const client = useReportExplorerClient();
  return useQueries({
    queries: descriptors.map(({ kind, request, enabled }) => ({
      queryKey: [...reportExplorerQueryKey, kind, request],
      queryFn: () => client.run(request),
      enabled,
    })),
  });
}

export function useReportFilterOptions(
  field: ReportFilterField,
  searchTerm: string,
  enabled: boolean,
  scope = ReportExplorerScope.Standard,
  publisherId?: string,
  demandIntegrationId?: string,
  fetchOnInit = false,
) {
  const client = useReportExplorerClient();
  const mode = reportFilterDefinitions[field].lookupMode;
  const normalizedSearch = searchTerm.trim();
  const serverSearchEnabled =
    mode === ReportFilterLookupMode.Server && (fetchOnInit || normalizedSearch.length > 0);
  const querySearch = mode === ReportFilterLookupMode.Server ? normalizedSearch : '';
  return useQuery({
    queryKey: [
      ...reportExplorerQueryKey,
      scope,
      publisherId ?? '',
      demandIntegrationId ?? '',
      'filter-options',
      field,
      querySearch,
    ],
    queryFn: () =>
      mode === ReportFilterLookupMode.Local
        ? client.listFilterOptions(field)
        : client.searchFilterOptions(
            field,
            normalizedSearch,
            scope,
            publisherId,
            demandIntegrationId,
          ),
    enabled: enabled && (mode === ReportFilterLookupMode.Local || serverSearchEnabled),
  });
}
